import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { BookOpen, Search, Star } from "lucide-react";

export default function Courses() {
  const [courses, setCourses] = useState([]);
  const [q, setQ] = useState("");
  const [category, setCategory] = useState("All");

  useEffect(() => { api.get("/courses").then((r) => setCourses(r.data)); }, []);

  const categories = useMemo(() => ["All", ...Array.from(new Set(courses.map((c) => c.category)))], [courses]);
  const filtered = courses.filter((c) => {
    const catOk = category === "All" || c.category === category;
    const qOk = !q || c.title.toLowerCase().includes(q.toLowerCase()) || c.description.toLowerCase().includes(q.toLowerCase());
    return catOk && qOk;
  });

  return (
    <div className="space-y-6" data-testid="courses-page">
      <div>
        <p className="text-xs uppercase tracking-[0.25em] text-gold font-semibold">Course Catalog</p>
        <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tight mt-2">All Programs</h1>
        <p className="text-muted-foreground mt-2 max-w-xl">16 premium programs designed to help you Learn • Implement • Earn.</p>
      </div>

      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input value={q} onChange={(e) => setQ(e.target.value)}
          data-testid="course-search" placeholder="Search programs..."
          className="pl-9 rounded-full h-11 bg-secondary/50 border-transparent" />
      </div>

      <div className="flex flex-wrap gap-2">
        {categories.map((c) => (
          <button key={c} onClick={() => setCategory(c)}
            data-testid={`filter-${c.toLowerCase().replace(/ /g, "-")}`}
            className={`rounded-full px-4 py-1.5 text-sm font-medium border transition ${
              category === c
                ? "bg-primary text-primary-foreground border-primary"
                : "bg-card border-border hover:border-primary/50"
            }`}>
            {c}
          </button>
        ))}
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {filtered.map((c) => (
          <Link to={`/app/courses/${c.slug}`} key={c.id}
                data-testid={`course-card-${c.slug}`}
                className="rounded-3xl bg-card border border-border overflow-hidden card-hover">
            <div className="relative">
              <img src={c.thumbnail} alt={c.title} className="w-full h-44 object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
              {c.featured && <span className="absolute top-3 right-3 gold-gradient text-[#1E3A8A] text-[10px] font-bold px-2.5 py-1 rounded-full uppercase tracking-widest">Featured</span>}
              <span className="absolute top-3 left-3 rounded-full bg-white/95 text-[#1E3A8A] text-[10px] font-bold px-2.5 py-1 uppercase tracking-widest">{c.category}</span>
            </div>
            <div className="p-5">
              <h3 className="font-display font-bold text-xl">{c.title}</h3>
              <p className="text-sm text-muted-foreground mt-1.5 line-clamp-2">{c.tagline}</p>
              <div className="mt-4 flex items-center justify-between text-xs text-muted-foreground">
                <span className="flex items-center gap-1"><BookOpen className="h-3 w-3" />{c.modules?.reduce((s, m) => s + m.lessons.length, 0) || 0} lessons</span>
                <span className="flex items-center gap-1 text-gold font-bold"><Star className="h-3 w-3 fill-gold" />{c.rating}</span>
              </div>
              <div className="mt-3 flex items-center justify-between text-xs">
                <span className="text-muted-foreground">{c.duration_hours}h · {c.difficulty}</span>
                <span className="text-muted-foreground">{c.students_enrolled}+ students</span>
              </div>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}
